import React, { useState } from 'react';
import type { Period, Session, Student, AttRec } from '../types';
import { D1, FONT, d1, fmtPin } from '../design/tokens';
import { useViewportWidth } from './TeacherShell';

export interface ChamadaProps {
  periods: Period[]; viewPeriodId: string; setViewPeriodId:(id:string)=>void; isViewingActive:boolean;
  session: Session|null; qrUrl:string; pin:string; timeLeft:number;
  onStartSession:(turma:string)=>void; onStopSession:(sess:Session)=>void;
  isOnline: boolean; darkMode: boolean;
  allSessions: Session[]; onReopenSession:(sessId:string)=>void;
  onDeleteSession:(sessDocId:string)=>void;
  onRenameSession:(s:Session)=>void;
  poolPinA:string;
  poolPinB:string;
  students?: Student[];
  attendance?: AttRec[];
}

const toMs = (x:any) => x?.toDate ? x.toDate().getTime() : new Date(x).getTime();
const fmtTime = (x:any, withSec:boolean) => {
  const t = toMs(x); if (isNaN(t)) return '';
  return new Date(t).toLocaleTimeString('pt-BR', withSec ? { hour:'2-digit', minute:'2-digit', second:'2-digit' } : { hour:'2-digit', minute:'2-digit' });
};
const fmtDate = (x:any) => { const t = toMs(x); return isNaN(t) ? '—' : new Date(t).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' }); };

// Segundos até a próxima troca de PIN (janelas fixas de 30 s)
function useCountdown() {
  const [n, setN] = useState(Date.now());
  React.useEffect(() => { const iv = setInterval(() => setN(Date.now()), 1000); return () => clearInterval(iv); }, []);
  return 30 - Math.floor((n % 30000) / 1000);
}

function PinCard({ id, turma, pin, isOnline, secs }: { id:string; turma:string; pin:string; isOnline:boolean; secs:number }) {
  return (
    <div id={id} style={{ background:'#fff', border:`1px solid ${D1.line}`, padding:28 }}>
      <div style={d1.cap}>PIN da chamada · Turma {turma}</div>
      <div style={{ fontFamily:FONT.mono, fontSize:52, fontWeight:600, letterSpacing:3, margin:'8px 0 14px', whiteSpace:'nowrap' }}>{fmtPin(pin)}</div>
      {isOnline ? (
        <>
          <div style={{ fontSize:13, color:D1.ink2, lineHeight:1.6 }}>
            PIN renovado a cada 30 s.<br/>Próxima troca em <b style={{ fontFamily:FONT.mono }}>00:{String(secs).padStart(2,'0')}</b>
          </div>
          <div style={{ height:3, background:D1.line2, marginTop:10, width:200, maxWidth:'100%' }}>
            <div style={{ height:'100%', width:`${((30 - secs) / 30) * 100}%`, background:D1.acc }} />
          </div>
        </>
      ) : (
        <div style={{ fontSize:13, color:D1.ink2 }}>Sem conexão: os alunos usam este PIN fixo.</div>
      )}
    </div>
  );
}

function Registros({ regs, total }: { regs:AttRec[]; total:number }) {
  const pct = total ? Math.min(100, Math.round((regs.length / total) * 100)) : 0;
  return (
    <div style={{ minWidth:0 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <h2 style={d1.h2}>Registros</h2>
        <span style={{ fontFamily:FONT.serif, fontSize:26, fontWeight:600 }}>{regs.length}<span style={{ fontSize:16, color:D1.ink3 }}> / {total}</span></span>
      </div>
      <div style={{ height:4, background:D1.line2, marginBottom:10 }}>
        <div style={{ height:'100%', width:`${pct}%`, background:D1.ok }} />
      </div>
      <div style={{ maxHeight:340, overflowY:'auto' }}>
        {regs.map((r, i) => (
          <div key={r.id || i} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'8px 0', borderBottom:`1px solid ${D1.line2}`, fontSize:13.5 }}>
            <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              <span style={{ fontFamily:FONT.mono, color:D1.ink3, marginRight:12 }}>{String(regs.length - i).padStart(2,'0')}</span>{r.studentName}
            </span>
            <span style={{ fontFamily:FONT.mono, color:D1.ink2 }}>{fmtTime(r.timestamp, true)}</span>
          </div>
        ))}
        {regs.length === 0 && <div style={{ fontSize:13.5, color:D1.ink2, padding:'8px 0' }}>Aguardando os primeiros registros…</div>}
      </div>
    </div>
  );
}

export function ChamadaTab(props: ChamadaProps) {
  const { isViewingActive, onStartSession, onStopSession, isOnline, allSessions, onReopenSession, onDeleteSession, poolPinA, poolPinB, onRenameSession } = props;
  const students = props.students || [];
  const attendance = props.attendance || [];
  const [histTab, setHistTab] = useState<'A'|'B'>('A');
  const vw = useViewportWidth();
  const secs = useCountdown();

  const ativas = (['A','B'] as const)
    .map(t => ({ turma:t, sess: allSessions.find(s => s.active && s.turma === t) || null, pin: t === 'A' ? poolPinA : poolPinB }))
    .filter(x => x.sess) as { turma:'A'|'B'; sess:Session; pin:string }[];
  const livres = (['A','B'] as const).filter(t => !ativas.some(a => a.turma === t));

  const ordemDaAula = (s:Session) => allSessions.filter(x => x.turma === s.turma).slice().sort((a,b) => toMs(a.createdAt) - toMs(b.createdAt)).findIndex(x => x.id === s.id) + 1;
  const totalAtivos = (t:string) => students.filter(s => s.turma === t && !s.blocked).length;
  const tela = (id:string) => { const el:any = document.getElementById(id); if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {}); };
  const historico = allSessions.filter(s => s.turma === histTab).slice().sort((a,b) => toMs(b.createdAt) - toMs(a.createdAt));

  return (
    <div style={{ paddingBottom:24 }}>
      {!isViewingActive && (
        <div style={{ background:'#faf4e1', color:'#7a5a12', border:'1px solid #e6d7a8', padding:'10px 14px', fontSize:13, marginBottom:18 }}>
          Você está vendo um período inativo. Chamadas abertas aqui ficam registradas nesse período.
        </div>
      )}

      {ativas.length === 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={d1.cap}>Nenhuma chamada aberta</div>
          <h1 style={{ ...d1.h1, marginTop:6 }}>Chamada</h1>
        </div>
      )}

      {ativas.map(({ turma, sess, pin }) => {
        const regs = attendance.filter(a => a.sessionId === sess.sessionId).slice().sort((a,b) => toMs(b.timestamp) - toMs(a.timestamp));
        const cardId = 'gf-pin-' + turma;
        return (
          <section key={sess.id} style={{ marginBottom:36 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:16, flexWrap:'wrap', marginBottom:20 }}>
              <div style={{ minWidth:0 }}>
                <div style={{ ...d1.cap, color:D1.ok }}>● Chamada aberta · {fmtTime(sess.createdAt, false)}</div>
                <h1 style={{ ...d1.h1, marginTop:6, fontSize: vw < 700 ? 26 : 34 }}>
                  Turma {turma} — Aula {String(ordemDaAula(sess)).padStart(2,'0')}{sess.tema ? `, ${sess.tema}` : ''}
                </h1>
              </div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <button style={d1.btnO} onClick={() => tela(cardId)}>Projetar em tela cheia</button>
                <button style={d1.btnR} onClick={() => onStopSession(sess)}>Encerrar chamada</button>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns: vw < 1100 ? '1fr' : '1.1fr 1fr', gap:28 }}>
              <PinCard id={cardId} turma={turma} pin={pin} isOnline={isOnline} secs={secs} />
              <Registros regs={regs} total={totalAtivos(turma)} />
            </div>
          </section>
        );
      })}

      {livres.length > 0 && (
        <div style={{ ...d1.card, marginBottom:32, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:200 }}>
            <h2 style={{ ...d1.h2, marginBottom:4 }}>Abrir chamada</h2>
            <div style={{ fontSize:13, color:D1.ink2 }}>Gera o PIN rotativo para a turma selecionada.</div>
          </div>
          {livres.map((t, i) => (
            <button key={t} disabled={!isViewingActive} onClick={() => onStartSession(t)}
              style={{ ...(i === 0 ? d1.btn : d1.btnO), opacity: isViewingActive ? 1 : 0.5, cursor: isViewingActive ? 'pointer' : 'not-allowed' }}>
              Turma {t}
            </button>
          ))}
        </div>
      )}

      {/* Histórico */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:12, marginBottom:6, flexWrap:'wrap' }}>
        <h2 style={d1.h2}>Histórico de chamadas</h2>
        <div style={{ display:'flex', border:`1px solid ${D1.line}`, borderRadius:3, overflow:'hidden', fontSize:13, background:'#fff' }}>
          {(['A','B'] as const).map((t, i) => (
            <button key={t} onClick={() => setHistTab(t)} style={{ padding:'7px 14px', border:'none', borderLeft: i ? `1px solid ${D1.line}` : 'none', background: histTab === t ? D1.acc : '#fff', color: histTab === t ? '#fff' : D1.ink2, cursor:'pointer', fontSize:13 }}>Turma {t}</button>
          ))}
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={d1.table}>
          <thead><tr>
            <th style={d1.th}>Aula</th><th style={d1.th}>Tema</th><th style={d1.th}>Data</th>
            <th style={{ ...d1.th, textAlign:'right' }}>Registros</th><th style={d1.th}></th>
          </tr></thead>
          <tbody>
            {historico.map(s => {
              const n = new Set(attendance.filter(a => a.sessionId === (s.sessionId || s.id)).map(a => a.studentId)).size;
              return (
                <tr key={s.id}>
                  <td style={{ ...d1.td, fontFamily:FONT.mono, color:D1.ink2 }}>{String(ordemDaAula(s)).padStart(2,'0')}</td>
                  <td style={d1.td}>{s.tema || <span style={{ color:D1.ink3 }}>(sem tema)</span>}{s.active && <span style={{ ...d1.cap, fontSize:10, color:D1.ok, marginLeft:8 }}>● aberta</span>}</td>
                  <td style={{ ...d1.td, fontFamily:FONT.mono, fontSize:12.5 }}>{fmtDate(s.createdAt)}</td>
                  <td style={{ ...d1.td, fontFamily:FONT.mono, textAlign:'right' }}>{n}</td>
                  <td style={{ ...d1.td, whiteSpace:'nowrap', textAlign:'right' }}>
                    <button onClick={() => onRenameSession(s)} style={{ ...d1.link, fontSize:12.5 }}>Editar</button>
                    <span style={{ color:D1.line, margin:'0 8px' }}>·</span>
                    <button onClick={() => onReopenSession(s.id)} style={{ ...d1.link, fontSize:12.5 }}>Reabrir</button>
                    <span style={{ color:D1.line, margin:'0 8px' }}>·</span>
                    <button onClick={() => onDeleteSession(s.id)} style={{ ...d1.link, fontSize:12.5, color:D1.alert }}>Excluir</button>
                  </td>
                </tr>
              );
            })}
            {historico.length === 0 && <tr><td colSpan={5} style={{ ...d1.td, color:D1.ink3, textAlign:'center' }}>Nenhuma chamada nesta turma.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
